import { Injectable } from "@angular/core";
import moment from "moment";
import { Helper } from "@helpers/functions";
import { Language } from "@models/language";
import { BehaviorSubject, Observable, of } from "rxjs";
import { config } from "@config";
import { LANGUAGE_ISO_CODES } from "@helpers/constants";
import { HttpClient } from "@angular/common/http";
import { tap } from "rxjs/operators";
import packageJson from "../../../../../package.json";
import { Translations } from "@core-services/models/translations.model";

@Injectable({
	providedIn: "root"
})
export class TranslationService {
	private lang: string = null;
	private static currentLang: string = null;
	private static availableLanguages: Language[] = [];
	public static cookieLanguage: string = Helper.getCookie("preferred-language");

	private static languages: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(["deu"]);
	private language$: BehaviorSubject<string> = new BehaviorSubject("eng");

	public constructor(private http: HttpClient) {
		// Define all momentJS languages
		LANGUAGE_ISO_CODES.forEach((isoCode: string, language: string) => {
			moment.defineLocale(language, { parentLocale: isoCode });
		});
	}

	public initLanguages(languages: Language[]): void {
		TranslationService.availableLanguages = languages;
		moment.locale("de"); // We need to load german once to extend it
		TranslationService.languages.next(languages.map((l: Language) => l.getId()));

		this.lang = TranslationService.startsWithNaviLanguage();
		TranslationService.currentLang = this.lang;

		this.updateMoment(this.lang, TranslationService.getLanguageIsoCode(this.lang));
		moment.locale(this.lang);

		// Identify and set the cookie language
		let preferredLanguage = TranslationService.cookieLanguage;
		LANGUAGE_ISO_CODES.forEach((iso2: string, iso3: string) => {
			if (preferredLanguage === iso3) {
				this.lang = preferredLanguage;
				TranslationService.currentLang = this.lang;
			}
		});

		this.updateHelperLanguage();

		this.loadLanguage(this.lang).subscribe(() => {
			// Only display the XSS message once the translations are loaded
			if (!config.debug) {
				console.log("%c " + this.translate("xss_warning_console"), "color: #ff0000; font-size: 20px");
			}
		});
	}

	public translate(key: string, values: (number | string)[] = null): string {
		if (key.trim().length === 0) {
			return;
		}

		let translation = key;

		if (!this[this.lang]) {
			return key;
		}

		if (this[this.lang].hasOwnProperty(key)) {
			translation = this[this.lang][key] || key;
		}

		if (!values) {
			return translation.length > 0 ? translation : key;
		}

		values = values.filter((val) => !!val);

		if (translation.includes("%1")) {
			// Predefined placeholder order
			values.forEach((val: number | string, index: number) => {
				translation = translation.replace(`%${index + 1}`, val.toString());
			});
		} else {
			// Legacy placeholder handling
			values.forEach((val: number | string) => {
				translation = translation.replace("%" + (typeof val === "number" ? "d" : "s"), val.toString());
			});
		}

		return translation.length > 0 ? translation : key;
	}

	public translateWithLang(lang: string, key: string, values: (number | string)[] = null): string {
		if (key.trim().length === 0) {
			return;
		}

		let translation = key;

		if (!this[lang]) {
			return key;
		}

		if (this[lang].hasOwnProperty(key)) {
			translation = this[lang][key] || key;
		}

		if (!values) {
			return translation.length > 0 ? translation : key;
		}

		values = values.filter((val) => !!val);

		if (translation.includes("%1")) {
			// Predefined placeholder order
			values.forEach((val: number | string, index: number) => {
				translation = translation.replace(`%${index + 1}`, val.toString());
			});
		} else {
			// Legacy placeholder handling
			values.forEach((val: number | string) => {
				translation = translation.replace("%" + (typeof val === "number" ? "d" : "s"), val.toString());
			});
		}

		return translation.length > 0 ? translation : key;
	}

	public translateMultiple(keys: string[]): Map<string, string> {
		const translations: Map<string, string> = new Map<string, string>();
		keys.forEach((key) => {
			translations.set(key, this.translate(key));
		});
		return translations;
	}

	private updateMoment(language: string, isoCode?: string): void {
		moment.updateLocale(language, { parentLocale: isoCode });
	}

	public loadCookieLanguage(language: string = TranslationService.cookieLanguage): void {
		if (language) {
			this.setLanguage(language);
		}
	}

	public setLanguage(language: string): void {
		// Language already loaded, so we can skip loading it again
		if (this.lang === language || !language) {
			return;
		}
		this.loadLanguage(language).subscribe();
	}

	public setLanguage$(language: string): Observable<Translations> {
		if (this.lang === language || !language) {
			return of();
		}
		return this.loadLanguage(language, false);
	}

	public loadLanguage(language: string, shouldUpdateLangSettings: boolean = true): Observable<Translations> {
		if (!LANGUAGE_ISO_CODES.get(language)) {
			language = "en";
		}
		let isoCode: string = TranslationService.getLanguageIsoCode(language);
		return this.http.get<Translations>(`/assets/locale/${isoCode}.json?v=${packageJson.version}`).pipe(
			tap((response) => {
				this[language] = response;
				if (shouldUpdateLangSettings) {
					this.updateLanguageSettings(language, isoCode);
				}
			})
		);
	}

	public getLanguage(): string {
		return this.lang;
	}

	public static getLanguageIsoCode(lang: string = TranslationService.currentLang): string {
		const language = TranslationService.AVAILABLE_LANGUAGES.find((l: Language) => l.getId() === lang);
		const cookieLang = LANGUAGE_ISO_CODES.get(this.cookieLanguage);

		if (!language && cookieLang) {
			return cookieLang;
		} else if (!language && !cookieLang) {
			return "en";
		}
		return language.getIsoCode();
	}

	public static startsWithNaviLanguage(): string {
		const language = TranslationService.AVAILABLE_LANGUAGES.find((lang: Language) =>
			navigator.language.startsWith(lang.getIsoCode())
		);

		if (!language) {
			return "eng";
		}

		return language.getId();
	}

	private updateHelperLanguage(): void {
		Helper.setLanguage(this.lang);
	}

	private updateLanguageSettings(lang: string, isoCode: string): void {
		this.lang = lang;
		TranslationService.currentLang = this.lang;
		Helper.setCookie("preferred-language", lang, true);
		if (Helper.getCookie("app-language")) {
			Helper.setCookie("app-language", lang, false, false, true, true, undefined, true, true);
		}
		this.updateMoment(lang, isoCode);
		this.updateHelperLanguage();
		this.language$.next(lang);
	}

	public static LanguagesObservable(): Observable<string[]> {
		return this.languages.asObservable();
	}

	public getHdLink(route: string = "/articles/"): string {
		const isoCode = TranslationService.getLanguageIsoCode();
		const languageShort: string = isoCode === "en" ? "en-us" : isoCode;
		return config.helpcenterUrl + "hc/" + languageShort + route;
	}

	public getEngDeuHdLink(route: string = "/articles/"): string {
		const isoCode = TranslationService.getLanguageIsoCode();
		const isoCodeEngDeu = isoCode !== "de" ? "en-us" : isoCode;
		return config.helpcenterUrl + "hc/" + isoCodeEngDeu + route;
	}

	public static get LANGUAGES(): string[] {
		return TranslationService.languages.getValue();
	}

	public static get AVAILABLE_LANGUAGES(): Language[] {
		return this.availableLanguages;
	}

	public languageUpdate$(): Observable<string> {
		return this.language$.asObservable();
	}
}
